import { ColumnDef } from "@tanstack/react-table";
import { IGamification, CampaignType, CAMPAIGN_TYPE_LABELS, GamificationSortBy, normalizeMechanic } from "../../../../@types/gamification";
import ActionTable from "../components/action-table";
import { SortableHeader } from "./components/sortable-header";
import { Badge } from "components/ui/badge";
import { Disc3, Ticket, Cherry, Goal, LucideIcon } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { FileText } from "lucide-react";
import { STATUS_COLORS, STATUS_LABELS } from "../utils/status";
import { formatDateString } from "@/utils/date-string";

export const columns: ColumnDef<IGamification>[] = [
  {
    accessorKey: "idCapa",
    header: () => (
      <SortableHeader field={GamificationSortBy.ID_CAPA} label="ID" />
    ),
    cell: ({ row }) => {
      const idCapa = row.getValue("idCapa") as number | undefined;

      if (!idCapa) {
        return <span className="text-sm text-gray-400">-</span>;
      }

      return <span className="text-sm">{idCapa}</span>;
    },
  },
  {
    accessorKey: "nickname",
    header: () => (
      <SortableHeader
        field={GamificationSortBy.CAMPAIGN_NAME}
        label="Nome da Campanha"
      />
    ),
    cell: ({ row }) => {
      return <span>{row.getValue("nickname")}</span>;
    },
  },
  {
    accessorKey: "campaignType",
    header: "Mecânica",
    cell: ({ row }) => {
      const type = normalizeMechanic(row.original.mechanic ?? row.getValue("campaignType"));
      const icons: Record<CampaignType, LucideIcon> = {
        ROULETTE: Disc3,
        SCRATCH: Ticket,
        SLOT: Cherry,
        PENALTY: Goal,
      };
      const Icon = icons[type];
      return (
        <Badge variant="outline" className="gap-1.5 bg-white text-gray-700">
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
          {CAMPAIGN_TYPE_LABELS[type]}
        </Badge>
      );
    },
  },
  {
    accessorKey: "startDate",
    header: () => (
      <SortableHeader
        field={GamificationSortBy.START_DATE}
        label="Data de Início"
      />
    ),
    cell: ({ row }) => {
      const date = row.getValue("startDate") as string;
      return formatDateString(date);
    },
  },
  {
    accessorKey: "endDate",
    header: () => (
      <SortableHeader field={GamificationSortBy.END_DATE} label="Data de Fim" />
    ),
    cell: ({ row }) => {
      const date = row.getValue("endDate") as string;
      return formatDateString(date);
    },
  },
  {
    accessorKey: "stores",
    header: "Lojas",
    cell: ({ row }) => {
      const stores = row.getValue("stores") as IGamification["stores"];
      const allStoresSelected = row.original.allStoresSelected;

      if (!stores || stores.length === 0) {
        return <span className="text-sm text-gray-500">Nenhuma loja</span>;
      }

      const isAllStores =
        allStoresSelected ||
        (stores.length === 1 && stores[0].storeCode === "0");

      if (isAllStores) {
        return (
          <span
            className="text-sm font-medium"
            title="Todas as lojas selecionadas"
          >
            Todas
          </span>
        );
      }

      const allCodes = stores.map((store) => store.storeCode).join(", ");
      const displayCodes =
        stores.length > 3
          ? `${stores
              .slice(0, 3)
              .map((store) => store.storeCode)
              .join(", ")}...`
          : allCodes;

      return (
        <span className="text-sm" title={`Lojas: ${allCodes}`}>
          {displayCodes}
        </span>
      );
    },
  },
  {
    accessorKey: "hasRegulationFile",
    header: () => <div className="text-center">Regulamento</div>,
    cell: ({ row }) => {
      const hasRegulationFile = row.getValue("hasRegulationFile") as boolean;
      return (
        <div
          className="flex justify-center items-center"
          title={
            hasRegulationFile
              ? "Arquivo de regulamento disponível"
              : "Nenhum arquivo de regulamento"
          }
        >
          <div
            className={`p-2 rounded-full ${
              hasRegulationFile ? "bg-green-100" : "bg-gray-100"
            }`}
          >
            <FileText
              className={`h-4 w-4 ${
                hasRegulationFile ? "text-green-700" : "text-gray-400"
              }`}
            />
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    header: () => (
      <SortableHeader field={GamificationSortBy.STATUS} label="Status" />
    ),
    cell: ({ row }) => {
      const status = row.getValue("status") as keyof typeof STATUS_COLORS;
      return (
        <Badge className={STATUS_COLORS[status]}>{STATUS_LABELS[status]}</Badge>
      );
    },
  },
  {
    accessorKey: "createdAt",
    header: () => (
      <SortableHeader field={GamificationSortBy.CREATED_AT} label="Criado em" />
    ),
    cell: ({ row }) => {
      const date = row.getValue("createdAt") as string;

      const parsed = date ? new Date(date) : null;
      if (!parsed || Number.isNaN(parsed.getTime())) {
        return <span className="text-sm text-gray-400">-</span>;
      }
      return format(parsed, "dd/MM/yyyy HH:mm", { locale: ptBR });
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      return <ActionTable row={row.original} />;
    },
  },
];
